// soundmgr.ts ↔ game/soundmgr.cpp — gsndm, the sound manager: maps the sfx
// data out of the pack (htsfx.pdb: "soundfiles" name list + per-sound .snd
// ADPCM records + the "SfxEntries" id→{sound,priority} table), allocates
// device channels by priority, and carries enable/volume state.
//
// Implements the ui/shellform.ts SoundMgr seam (the gsndm slice the session
// hosts already thread), so a session/shell can swap its MemorySoundMgr for
// this via the existing `sndm` option — no host edits required beyond
// construction + Init().
//
// Seams: the SoundDevice arrives via a FACTORY (HostOpenSoundDevice — Init
// opens it, RestoreState re-opens it after SaveStateAndClear dropped it);
// the pack is PackFileReader (gpakr); variant picks (`sfxc:<n>` ids from the
// SimUIHost.PlaySfx convention, fingerhandler.ts:124) resolve through
// SfxFromCategory over the injected ASYNC rng (glAsyncSeed ↔
// SimContext.rngAsync — sim-quirks B19; never the sync LCG).

import type { PackFileReader } from '../io/packfile.ts';
import type { SoundMgr as SoundMgrSeam } from '../ui/shellform.ts';
import { Rng } from '../../../sim/src/math/rng.ts';
import type { SoundDevice } from './sounddevice.ts';
import { SfxFromCategory } from './sfx.ts';

// ht.h:8344
export const kcChannelsMax = 16;
// ht.h:8364 — SfxEntry { byte nSound; byte nChannel (unused); byte nPriority }
export const kcbSfxEntry = 3;

/** ht.h:8352 PcmHeader — one mapped sound: its ADPCM bytes + size. */
export interface PcmHeader {
  // pcmh
  pb: Uint8Array | null;
  cb: number; // word — the C++ truncates the dword MapFile size to 16 bits
}

/** The host seams: HostOpenSoundDevice + HostSleep (WaitSilence only). */
export interface SoundMgrHost {
  /** HostOpenSoundDevice (soundmgr.cpp:29/130) — null = no audio. */
  openSoundDevice(): SoundDevice | null;
  /** HostSleep(ct) in 1/100s ticks (soundmgr.cpp:226) — only WaitSilence
   *  awaits it; omit for an immediate-resolve (still bounded) drain. */
  sleep?(ct: number): Promise<void>;
  /** The WiAutoLogSfx mirror (soundmgr.cpp PlaySfx's WI_AUTOMATION entry
   *  hook): every play ATTEMPT, post-`sfxc:` resolve but BEFORE the
   *  device/enable gates — the sfx-event oracle + hermetic-test log seam.
   *  Production hosts omit it. */
  onPlaySfx?(sfx: number): void;
}

export class SoundMgr implements SoundMgrSeam {
  // sndm (ht.h:8366)
  // soundmgr.cpp:7-18 SoundMgr::SoundMgr
  private m_cChannels = 0;
  private m_anPriorityChannel = new Uint8Array(kcChannelsMax);
  private m_apcmh: PcmHeader[] | null = null;
  private m_cpcmh = 0;
  private m_asfxe: Uint8Array | null = null; // the raw SfxEntries bytes
  private m_csfxe = 0;
  private m_psndd: SoundDevice | null = null;
  private m_fStateSaved = false;
  private m_fEnabledSav = false;
  private m_nVolumeSav = -1;
  // (m_afmap/FileMap bookkeeping is GC-collapsed, like the rest of the port)

  private readonly pakr: PackFileReader; // gpakr
  private readonly host: SoundMgrHost;
  /** glAsyncSeed (misc.cpp:610) — the session's SimContext.rngAsync so voice
   *  picks share the ONE async stream with the sim's serialized picks
   *  (Replicator.cpp:370); defaults to a private instance for shell-only use.
   *  GameSession adopts its sim's via SetRngAsync. */
  private rngAsync: Rng;

  constructor(pakr: PackFileReader, host: SoundMgrHost, rngAsync?: Rng) {
    this.pakr = pakr;
    this.host = host;
    this.rngAsync = rngAsync ?? new Rng();
  }

  /** Adopt a session's SimContext.rngAsync (the SoundMgr seam's optional
   *  member — gamesession.ts calls it at construction). In the C++ there is
   *  exactly one glAsyncSeed global; per-session adoption keeps the sim's
   *  serialized SfxFromCategory draws (replicator) and the PlaySfx-site
   *  `sfxc:` resolutions on the same stream, in call order. */
  SetRngAsync(rngAsync: Rng): void {
    this.rngAsync = rngAsync;
  }

  // soundmgr.cpp:25-87 SoundMgr::Init
  Init(): boolean {
    // Open the sound device
    this.m_psndd = this.host.openSoundDevice();
    if (this.m_psndd === null) return false;

    const pbFiles = this.pakr.MapFile('soundfiles');
    if (pbFiles === null) return false;

    // Load sounds - get count and load arrays (count is big-endian, :41)
    let fSuccess = true;
    this.m_cpcmh = (pbFiles[0] << 8) | pbFiles[1]; // BigWord(*(word *)pbFiles)
    this.m_apcmh = [];
    for (let i = 0; i < this.m_cpcmh; i++) this.m_apcmh.push({ pb: null, cb: 0 });

    // Map the sound files (:56-69) — psz walks the NUL-separated name list
    let psz = 2;
    for (let i = 0; i < this.m_cpcmh; i++) {
      let sz = '';
      let p = psz;
      while (p < pbFiles.length && pbFiles[p] !== 0) {
        sz += String.fromCharCode(pbFiles[p++]);
      }
      const pb = this.pakr.MapFile(sz);
      if (pb === null) {
        fSuccess = false;
        break;
      }
      // :66 m_apcmh[i].cb = (word)cb — the C++ stores the dword size in a
      // word, so PlayAdpcm always plays the truncated length; applied ONCE
      // here so every PlaySfx hands the device the SAME Uint8Array (the
      // webaudiodev decode cache keys on identity). Shipped sounds top out
      // ~14KB so the truncation never bites real data.
      const cb = pb.length & 0xffff;
      this.m_apcmh[i].pb = cb === pb.length ? pb : pb.subarray(0, cb);
      this.m_apcmh[i].cb = cb;
      if (i < this.m_cpcmh - 1) psz = p + 1; // psz += strlen(psz) + 1
    }
    if (!fSuccess) return false;

    // Map SfxEntries (:74-80)
    const asfxe = this.pakr.MapFile('SfxEntries');
    if (asfxe === null) return false;
    this.m_asfxe = asfxe;
    this.m_csfxe = Math.floor(asfxe.length / kcbSfxEntry);

    // Turn on sound device (:82-84)
    this.m_cChannels = this.m_psndd.GetChannelCount();

    return true;
  }

  // soundmgr.cpp:89-110 SoundMgr::Exit
  Exit(): void {
    this.m_psndd?.dispose?.(); // delete m_psndd (the virtual dtor)
    this.m_psndd = null;

    // :94-104 — unmap + free the pcm headers (GC does the unmapping)
    this.m_apcmh = null;
    this.m_cpcmh = 0;

    // :106-109
    this.m_asfxe = null;
    this.m_csfxe = 0;
  }

  // soundmgr.cpp:112-116 SoundMgr::Enable
  Enable(fEnable: boolean): void {
    if (this.m_psndd !== null) {
      this.m_psndd.Enable(fEnable);
    }
  }

  // soundmgr.cpp:118-123 SoundMgr::IsEnabled
  IsEnabled(): boolean {
    if (this.m_psndd !== null) {
      return this.m_psndd.IsEnabled();
    }
    return false;
  }

  // soundmgr.cpp:125-134 SoundMgr::RestoreState
  RestoreState(): void {
    if (this.m_fStateSaved) {
      this.m_fStateSaved = false;
      if (this.m_psndd === null) {
        this.m_psndd = this.host.openSoundDevice(); // HostOpenSoundDevice
      }
      this.Enable(this.m_fEnabledSav);
      this.SetVolume(this.m_nVolumeSav);
    }
  }

  // soundmgr.cpp:136-146 SoundMgr::SaveStateAndClear
  SaveStateAndClear(): boolean {
    if (this.m_fStateSaved) return false;
    this.m_fStateSaved = true;
    this.m_fEnabledSav = this.IsEnabled();
    this.m_nVolumeSav = this.GetVolume();
    this.m_psndd?.dispose?.(); // delete m_psndd
    this.m_psndd = null;
    return true;
  }

  // soundmgr.cpp:148-213 SoundMgr::PlaySfx.
  // Accepts the SimUIHost.PlaySfx union (fingerhandler.ts:126): a plain Sfx id,
  // or `sfxc:<n>` marking SfxFromCategory(n) — the async-rng variant pick the
  // C++ does AT THE CALL SITE (e.g. SimUI.cpp:2332), resolved here so every
  // host seam can forward verbatim.
  PlaySfx(sfx: number | string): void {
    if (typeof sfx === 'string') {
      if (!sfx.startsWith('sfxc:')) return;
      sfx = SfxFromCategory(parseInt(sfx.slice(5), 10), this.rngAsync);
    }
    // TS-only crash-class guard (parity-audit #9): a host seam handing a
    // non-finite value (undefined/NaN from a missed consts assignment) would
    // coerce `sfx | 0` → 0 below and PLAY sfx id 0 — a wrong-sound symptom
    // with no error. The C++ can't receive one (int-typed); -1 category
    // misses are finite and still flow to the hook + the nSfx<0 refusal.
    if (!Number.isFinite(sfx)) return;
    // the C++ WI_AUTOMATION hook position: every attempt (incl. a -1 category
    // miss), resolved, BEFORE the device/enable gates
    this.host.onPlaySfx?.(sfx);

    // Get the sfx entry and channel (:152-161)
    if (this.m_psndd === null) return;
    if (!this.m_psndd.IsEnabled()) return;
    const nSfx = sfx | 0;
    // :157 — the C++ check is `nSfx > m_csfxe` (NOT >=): nSfx == m_csfxe slips
    // through to an out-of-bounds SfxEntry read (UB). Branch preserved, but the
    // OOB read itself is refused below instead of reading heap garbage.
    if (nSfx < 0 || nSfx > this.m_csfxe) return;
    const ibSfxe = nSfx * kcbSfxEntry;
    if (this.m_asfxe === null || ibSfxe + kcbSfxEntry > this.m_asfxe.length) {
      return; // the nSfx == m_csfxe UB arm
    }
    const nSound = this.m_asfxe[ibSfxe]; // SfxEntry.nSound
    const nPriority = this.m_asfxe[ibSfxe + 2]; // SfxEntry.nPriority
    if (nSound === 0xff) return; // :160

    // Find a free channel, a channel playing the same priority sound effect,
    // or channel playing a lower priority sound effect (:163-199; the #if 0
    // same-priority-replacement scan at :171-182 stays dead)
    let ichnlUse = -1;
    let nPriorityLowest = 0;

    // If not playing, see if there is an empty channel. Keep track of lowest
    // priority channel
    if (ichnlUse === -1) {
      for (let ichnl = 0; ichnl < this.m_cChannels; ichnl++) {
        if (this.m_psndd.IsChannelFree(ichnl)) {
          nPriorityLowest = 255;
          ichnlUse = ichnl;
          break;
        }
        if (this.m_anPriorityChannel[ichnl] >= nPriorityLowest) {
          nPriorityLowest = this.m_anPriorityChannel[ichnl];
          ichnlUse = ichnl;
        }
      }
    }

    // We need to replace a lower priority sound effect
    // (higher numbers are lower priority)
    // :204 Assert(ichnlUse != -1) — only a 0-channel device gets here; the
    // typed-array write below discards the -1 index and the device rejects it,
    // mirroring the C++ release no-op.
    if (nPriority > nPriorityLowest) return;
    this.m_anPriorityChannel[ichnlUse] = nPriority;

    // Play the sound (:209-212) — pb is already the (word)cb-truncated view
    const pcmh = this.m_apcmh![nSound];
    this.m_psndd.PlayAdpcm(ichnlUse, pcmh.pb!);
  }

  // soundmgr.cpp:215-229 SoundMgr::WaitSilence — best-effort BOUNDED drain of
  // the playing sound (the C++ spins HostSleep(1)+HostSoundServiceProc; the
  // web port awaits the host sleep seam — callers await it where game.cpp:1770
  // called it synchronously).
  async WaitSilence(): Promise<void> {
    if (this.m_psndd === null) return;
    const kcWaitSilenceMax = 200; // ~2s of HostSleep(1)
    for (let c = 0; c < kcWaitSilenceMax && !this.m_psndd.IsSilent(); c++) {
      if (this.host.sleep) await this.host.sleep(1); // HostSleep(1)
      this.m_psndd.ServiceProc(); // HostSoundServiceProc
    }
  }

  // soundmgr.cpp:231-247 SoundMgr::FilterSleepTicks — the web build defines
  // SDL (makefile.sdl:262), so the don't-sleep-while-audible arm at :238-245
  // is compiled OUT: ticks pass through unchanged (event.ts:107 notes
  // identity is the expected web behavior).
  FilterSleepTicks(ct: number): number {
    return ct;
  }

  // soundmgr.cpp:249-253 SoundMgr::SetVolume
  SetVolume(nVolume: number): void {
    if (this.m_psndd !== null) {
      this.m_psndd.SetVolume(nVolume);
    }
  }

  // soundmgr.cpp:255-260 SoundMgr::GetVolume
  GetVolume(): number {
    if (this.m_psndd !== null) {
      return this.m_psndd.GetVolume();
    }
    return -1;
  }
}
